import React, { createRef } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { faBarcode } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import FieldTitle from './FieldTitle';
import ThemeIcons from '../../../theme/Icons';

const StyledWrapper = styled.div`
  height: 100%;
  width: 100%;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}
`;

const StyledInternalWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledBarcodeWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: ${(props) => props.padding || '3px'};
  border: ${(props) => props.borderWidth || 1}px solid ${(props) => props.borderColor || '#ccc'};
  border-radius: ${(props) => `${ props.borderRadius}px` || '4px'};
  background-color: ${(props) => props.backgroundColor || 'transparent'};
`;

const StyledBarcodeWrapDisplay = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: ${(props) => props.padding || '3px'};
  border: ${(props) => props.borderWidth || 1}px solid ${(props) => props.borderColor || '#ccc'};
  border-radius: ${(props) => `${props.borderRadius}px` || '4px'};
  background-color: ${(props) => props.backgroundColor || 'transparent'};
  min-height: 25px;
  box-sizing: border-box;
  cursor: pointer;

  &:hover {
    ${(props) => (props.disabled ? '' : `border-color: ${props.primaryColor}`)};
  }
`;

const StyledBarcodeDisplayText = styled.span`
  flex: 1;
  min-width: 0;
  font-size: ${(props) => props.fontSize}px;
  line-height: 1.4;
  color: ${(props) => props.fontColor};
  font-style: ${(props) => props.fontStyle};
  font-weight: ${(props) => props.fontWeight};
  text-align: ${(props) => props.textAlign};
  text-transform: ${(props) => props.textTransform};
  text-decoration: ${(props) => props.textDecoration};
  white-space: pre-line;
  overflow-wrap: break-word;
  word-break: break-word;
`;

const StyledInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  border: 0px;
  border-radius: ${(props) => `${props.borderRadius}px`};
  border-color: transparent;
  background-color: ${(props) => props.backgroundColor ? props.backgroundColor : 'transparent'};
  font-size: ${(props) => props.fontSize};
  color: ${(props) => props.fontColor};
  font-style: ${(props) => props.fontStyle};
  font-weight: ${(props) => props.fontWeight};
  text-align: ${(props) => props.textAlign};
  text-transform: ${(props) => props.textTransform};
  text-decoration: ${(props) => props.textDecoration};
  text-overflow: ${(props) => props.textOverflow === 'ellipsis' ? 'ellipsis' : 'clip'};
  resize: none;

  border: 1px solid transparent;

  &:hover, &:focus, &:focus-visible {
    outline: unset;
    border: 1px solid transparent;
  }

`;

const StyledWrapTextarea = styled.textarea`
  flex: 1;
  min-width: 0;
  border: none;
  padding: 0;
  border-radius: ${(props) => `${props.borderRadius}px`};
  background-color: ${(props) => props.backgroundColor ? props.backgroundColor : 'transparent'};
  font-size: ${(props) => props.fontSize}px;
  line-height: 1.4;
  color: ${(props) => props.fontColor};
  font-style: ${(props) => props.fontStyle};
  font-weight: ${(props) => props.fontWeight};
  text-align: ${(props) => props.textAlign};
  text-transform: ${(props) => props.textTransform};
  text-decoration: ${(props) => props.textDecoration};
  resize: none;
  overflow: hidden;
  white-space: pre-line;
  overflow-wrap: break-word;
  word-break: break-word;
  font-family: inherit;

  &:hover, &:focus, &:focus-visible {
    outline: none;
    border: none;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  cursor: pointer;
  background: transparent;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

/**
 * Why didn't I use a functional component here?
 *
 * The unmount functionality with the useEffect doesn't allow me
 * to access the focused and changes state that I needed. Inside
 * of the useEffect cleanup it always showed both these values
 * as undefined.
 *
 */
class BarcodeGenericField extends React.PureComponent {

  static propTypes = {

    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,

    disabled: PropTypes.bool,
    inputGroupWrapMode: PropTypes.bool,

    wrapperStyles: PropTypes.object,

    //SDK Styles
    primaryColor: PropTypes.string,

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,

    //Field style
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,

    //Action handlers
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,

    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ])

  }

  static defaultProps = {
    value: '',
    wrapperStyles: {}
  }

  constructor(props) {
    super(props);
    const { value } = props;
    this.state = { focused: false, changes: value };
    this.textareaRef = createRef();
    this.wrapTextareaRef = createRef();
    this.wrapDisplayHeight = null;
  }

  componentDidUpdate(prevProps, prevState) {
    const { changes } = this.state;
    const { value } = this.props;
    if (value !== prevProps.value && value !== changes) this.setState({ changes: value });
    if (prevState.changes !== changes || prevState.focused !== this.state.focused) {
      this.adjustWrapTextareaHeight();
    }
  }

  adjustWrapTextareaHeight = () => {
    if (this.wrapTextareaRef.current) {
      const el = this.wrapTextareaRef.current;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }
  };

  componentWillUnmount() {

    const { value, disabled, onChange, fieldId, fieldIdentifier, fieldPositionId, onBlur } = this.props;
    const { changes, focused } = this.state;

    /**
     * This ensures that changes are saved when this component unmounts
     * without first losing focus. The return func in useEffect is the
     * same as firing cleanup stuff in comoponentWillUnmount.
     */
    if (focused && changes !== value && !disabled) {

      onChange(fieldId, fieldIdentifier, fieldPositionId, changes);

      if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});

    }

  }

  render() {

    const { focused, changes } = this.state;

    const {
      wrapperStyles,

      fieldId,
      fieldIdentifier,
      fieldPositionId,

      title,
      value,
      disabled,

      primaryColor,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleTextAlign,
      titleFontStyle,
      titleFontWeight,
      titleTextTransform,
      titleTextDecoration,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,

      //Action handlers
      onChange,
      onFocus,
      onBlur,

      inputGroupWrapMode,

    } = this.props;

    const isWrapMode = inputGroupWrapMode && textOverflow !== 'ellipsis';

    const wrapperBorderProps = {
      borderRadius,
      borderColor,
      borderWidth,
      backgroundColor,
      padding,
      primaryColor,
    };

    const typographyProps = {
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
    };

    let barcodeNode;

    if (isWrapMode) {

      if (focused && !disabled) {
        barcodeNode = (
          <StyledBarcodeWrapDisplay
            disabled={disabled}
            {...wrapperBorderProps}
          >
            <StyledWrapTextarea
              ref={(e) => {
                this.wrapTextareaRef.current = e;
                this.textareaRef.current = e;
                if (e) {
                  e.style.height = 'auto';
                  e.style.height = `${e.scrollHeight}px`;
                }
              }}
              rows={1}
              value={changes}
              autoFocus
              {...{
                ...typographyProps,
                borderRadius,
                backgroundColor,
              }}
              onChange={(e) => {
                this.setState({ changes: e.target.value });
              }}
              onFocus={(e) => {
                if (!focused) this.setState({ focused: true });
                if (!disabled && onFocus) {
                  onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: () => { e.target.blur(); } });
                }
              }}
              onBlur={(e) => {
                const nextValue = e.target.value;
                if (focused) this.setState({ focused: false });
                if (!disabled && value !== nextValue) {
                  onChange(fieldId, fieldIdentifier, fieldPositionId, nextValue);
                }
                if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});
              }}
            />
            {disabled ? null : (
              <IconWrapper onClick={this.handleBarcodeClick}>
                <FontAwesomeIcon icon={ThemeIcons.fields.barcode} size="lg" />
              </IconWrapper>
            )}
          </StyledBarcodeWrapDisplay>
        );
      } else {
        barcodeNode = (
          <StyledBarcodeWrapDisplay
            data-ig-wrap-display="true"
            disabled={disabled}
            {...wrapperBorderProps}
            onClick={() => {
              if (!disabled) {
                this.setState({ focused: true });
              }
            }}
          >
            <StyledWrapTextarea
              readOnly
              tabIndex={-1}
              ref={(e) => {
                if (e) {
                  this.wrapDisplayHeight = e.parentElement ? e.parentElement.offsetHeight : null;
                  e.style.height = 'auto';
                  e.style.height = `${e.scrollHeight}px`;
                }
              }}
              rows={1}
              value={changes != null ? String(changes) : ''}
              style={{ pointerEvents: 'none', cursor: 'default' }}
              {...{
                ...typographyProps,
                borderRadius,
                backgroundColor,
              }}
            />
            {disabled ? null : (
              <IconWrapper onClick={this.handleBarcodeClick}>
                <FontAwesomeIcon icon={ThemeIcons.fields.barcode} size="lg" />
              </IconWrapper>
            )}
          </StyledBarcodeWrapDisplay>
        );
      }

    } else {

      barcodeNode = (
        <StyledBarcodeWrapper {...wrapperBorderProps}>
          <StyledInput
            ref={this.textareaRef}
            value={changes}
            {...{
              primaryColor,
              disabled,
              ...typographyProps,
              textOverflow,
              borderColor,
              borderRadius,
              borderWidth,
              backgroundColor,
              padding,
            }}
            onChange={(e) => {
              this.setState({ changes: e.target.value });
            }}
            onFocus={(e) => {

              if (!focused) this.setState({ focused: true });

              if (!disabled && onFocus && !focused) {

                onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: () => { e.target.blur(); } });

              }

            }}
            onBlur={(e) => {

              const nextValue = e.target.value;

              if (focused) this.setState({ focused: false });

              if (!disabled && value !== nextValue) {
                onChange(fieldId, fieldIdentifier, fieldPositionId, nextValue);
              }

              if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});
            }}
          />
          {disabled ? null : (
            <IconWrapper onClick={this.handleBarcodeClick}>
              <FontAwesomeIcon icon={ThemeIcons.fields.barcode} size="lg" />
            </IconWrapper>
          )}
        </StyledBarcodeWrapper>
      );

    }

    return (
      <StyledWrapper {...{ margin }} style={wrapperStyles || {}}>
        <StyledInternalWrapper>
          <FieldTitle
            {...{
              title,
              titleDisplay,
              titleFontSize,
              titleFontColor,
              titleTextAlign,
              titleFontStyle,
              titleFontWeight,
              titleTextTransform,
              titleTextDecoration,
            }}
          />
          {barcodeNode}
        </StyledInternalWrapper>
      </StyledWrapper>

    );
  }

  handleBarcodeClick = async () => {

    const { onCapture, onChange, fieldId, fieldIdentifier, fieldPositionId, value } = this.props;

    const response = await onCapture();

    if (this.textareaRef.current) {
      this.textareaRef.current.focus();
    }


    this.setState({ changes: response }, () => {
      const { changes } = this.state;

      if (onChange && changes !== value) {
        onChange(fieldId, fieldIdentifier, fieldPositionId, changes);
      }
    });
  }
}

export default BarcodeGenericField;
